// src/components/themes/default/store/BranchStorefront.tsx
"use client";

import { useState, useEffect } from "react";
import { Zap, Star, Flame, Package } from "lucide-react";

import { BranchCartProvider } from "./branch/BranchCartContext";
import BranchNavbar from "./branch/BranchNavbar";
import BranchHeader from "./branch/BranchHeader";

import BranchHeroBanner from "./branch/BranchHeroBanner";
import BranchCategoryBubbles from "./branch/BranchCategoryBubbles";
import BranchProductCard from "./branch/BranchProductCard";
import BranchCartDrawer from "./branch/BranchCartDrawer";
import BranchAuthModal from "./branch/BranchAuthModal";
import BranchCustomerDashboard from "./branch/BranchCustomerDashboard";
import BranchCheckoutView from "./branch/BranchCheckoutView";
import BranchProductDetailView from "./branch/BranchProductDetailView";

interface BranchStorefrontProps {
  branchData?: any;
  subdomain: string;
}

export default function BranchStorefront({ branchData, subdomain }: BranchStorefrontProps) {
  const rawBranch =
    branchData?.branch ||
    (branchData?.slug ? branchData : null) ||
    branchData?.branchInfo ||
    {};

  const branch = {
    id: Number(rawBranch.id || 0),
    name: String(rawBranch.name || "Branch Outlet"),
    slug: String(rawBranch.slug || ""),
    contact_phone: String(rawBranch.contact_phone || ""),
    address: String(rawBranch.address || ""),
  };

  const theme_config = branchData?.theme_config || {};
  const categories = Array.isArray(branchData?.categories) ? branchData.categories : [];
  const products = Array.isArray(branchData?.products) ? branchData.products : [];
  const featured_products = Array.isArray(branchData?.featured_products) ? branchData.featured_products : [];
  const bestseller_products = Array.isArray(branchData?.bestseller_products) ? branchData.bestseller_products : [];
  const deals_of_the_day = Array.isArray(branchData?.deals_of_the_day) ? branchData.deals_of_the_day : [];

  const primaryColor = theme_config?.primary_color || "#059669";
  const [selectedCategory, setSelectedCategory] = useState<string>("all");
  const [searchQuery, setSearchQuery] = useState("");

  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);
  const [branchUser, setBranchUser] = useState<any | null>(null);

  const [isCustomerDashboardOpen, setIsCustomerDashboardOpen] = useState(false);
  const [isCheckoutViewOpen, setIsCheckoutViewOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<any | null>(null);

  useEffect(() => {
    if (typeof window === "undefined" || !branch.id) return;
    try {
      const savedUser = localStorage.getItem(`branch_customer_${branch.id}`);
      if (savedUser) setBranchUser(JSON.parse(savedUser));
    } catch {
      setBranchUser(null);
    }
  }, [branch.id]);

  const handleLogout = () => {
    if (branch.id) {
      localStorage.removeItem(`branch_customer_${branch.id}`);
      localStorage.removeItem(`branch_customer_token_${branch.id}`);
    }
    localStorage.removeItem("customer_user");
    localStorage.removeItem("customer_token");
    setBranchUser(null);
    setIsCustomerDashboardOpen(false);
    setIsCheckoutViewOpen(false);
    setSelectedProduct(null);
  };

  const filteredProducts = products.filter((p: any) => {
    const matchSearch =
      p.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      p.sku?.toLowerCase().includes(searchQuery.toLowerCase());

    if (selectedCategory === "all") return matchSearch;

    const activeCatObj = categories.find(
      (c: any) => String(c.id) === selectedCategory || c.slug === selectedCategory
    );

    const matchCategory =
      String(p.category_id) === selectedCategory ||
      String(p.category) === selectedCategory ||
      (activeCatObj && String(p.category_id) === String(activeCatObj.id)) ||
      p.category_slug === selectedCategory ||
      (activeCatObj && p.category_slug === activeCatObj.slug) ||
      (activeCatObj && p.category_name?.toLowerCase() === activeCatObj.name?.toLowerCase()) ||
      p.category_name?.toLowerCase() === selectedCategory.toLowerCase();

    return matchSearch && matchCategory;
  });

  return (
    <BranchCartProvider subdomain={subdomain} branchId={branch.id}>
      {/* 🌟 1. Product Details View (branchUser shoho shob state pass kora hoyeche) */}
      {selectedProduct ? (
        <BranchProductDetailView
          product={selectedProduct}
          branch={branch}
          subdomain={subdomain}
          primaryColor={primaryColor}
          branchUser={branchUser}
          announcementText={theme_config?.announcement_text}
          onOpenAuth={() => setIsAuthModalOpen(true)}
          onLogout={handleLogout}
          onOpenCustomerDashboard={() => setIsCustomerDashboardOpen(true)}
          onBack={() => setSelectedProduct(null)}
          onProceedToCheckout={() => {
            setSelectedProduct(null);
            setIsCheckoutViewOpen(true);
          }}
        />
      ) : isCheckoutViewOpen ? (
        /* 2. Checkout View */
        <BranchCheckoutView
          branch={branch}
          customerUser={branchUser}
          subdomain={subdomain}
          primaryColor={primaryColor}
          onBack={() => setIsCheckoutViewOpen(false)}
          onOrderSuccess={() => {
            setIsCheckoutViewOpen(false);
            if (branchUser) {
              setIsCustomerDashboardOpen(true);
            }
          }}
        />
      ) : isCustomerDashboardOpen && branchUser ? (
        /* 3. Customer Dashboard */
        <BranchCustomerDashboard
          branch={branch}
          customerUser={branchUser}
          subdomain={subdomain}
          primaryColor={primaryColor}
          onBackToStore={() => setIsCustomerDashboardOpen(false)}
          onLogout={handleLogout}
        />
      ) : (
        /* 4. Storefront Catalog */
        <div
          style={{ ["--branch-primary" as any]: primaryColor }}
          className="min-h-screen bg-[#f8fafc] text-slate-800 font-sans selection:bg-emerald-500 selection:text-white"
        >
          <BranchNavbar
            branch={branch}
            announcementText={theme_config?.announcement_text}
            primaryColor={primaryColor}
          />

          <BranchHeader
            branch={branch}
            primaryColor={primaryColor}
            searchQuery={searchQuery}
            setSearchQuery={setSearchQuery}
            onOpenAuth={() => setIsAuthModalOpen(true)}
            branchUser={branchUser}
            onLogout={handleLogout}
            onOpenCustomerDashboard={() => setIsCustomerDashboardOpen(true)}
          />

          <BranchHeroBanner branchName={branch.name} primaryColor={primaryColor} />

          <BranchCategoryBubbles
            categories={categories}
            selectedCategory={selectedCategory}
            onSelectCategory={setSelectedCategory}
          />

          {deals_of_the_day.length > 0 && (
            <section className="max-w-7xl mx-auto px-4 sm:px-6 pt-10 space-y-4">
              <div className="flex items-center gap-2">
                <Zap className="w-5 h-5 text-purple-600 animate-pulse" />
                <h3 className="text-base sm:text-lg font-black text-slate-900">
                  Deals of the Day
                </h3>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {deals_of_the_day.map((p: any) => (
                  <BranchProductCard
                    key={p.id}
                    product={p}
                    primaryColor={primaryColor}
                    onSelectProduct={(prod) => setSelectedProduct(prod)}
                    onBuyNowClick={() => setIsCheckoutViewOpen(true)}
                  />
                ))}
              </div>
            </section>
          )}

          {featured_products.length > 0 && (
            <section className="max-w-7xl mx-auto px-4 sm:px-6 pt-10 space-y-4">
              <div className="flex items-center gap-2">
                <Star className="w-5 h-5 text-cyan-600" />
                <h3 className="text-base sm:text-lg font-black text-slate-900">
                  Featured Items
                </h3>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {featured_products.map((p: any) => (
                  <BranchProductCard
                    key={p.id}
                    product={p}
                    primaryColor={primaryColor}
                    onSelectProduct={(prod) => setSelectedProduct(prod)}
                    onBuyNowClick={() => setIsCheckoutViewOpen(true)}
                  />
                ))}
              </div>
            </section>
          )}

          {bestseller_products.length > 0 && (
            <section className="max-w-7xl mx-auto px-4 sm:px-6 pt-10 space-y-4">
              <div className="flex items-center gap-2">
                <Flame className="w-5 h-5 text-amber-500" />
                <h3 className="text-base sm:text-lg font-black text-slate-900">
                  Best Sellers
                </h3>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {bestseller_products.map((p: any) => (
                  <BranchProductCard
                    key={p.id}
                    product={p}
                    primaryColor={primaryColor}
                    onSelectProduct={(prod) => setSelectedProduct(prod)}
                    onBuyNowClick={() => setIsCheckoutViewOpen(true)}
                  />
                ))}
              </div>
            </section>
          )}

          <section className="max-w-7xl mx-auto px-4 sm:px-6 py-12 space-y-6">
            <div className="flex items-center justify-between border-b border-slate-200 pb-3">
              <div className="flex items-center gap-2">
                <Package className="w-5 h-5 text-emerald-600" />
                <h3 className="text-lg font-black text-slate-900">All Branch Products</h3>
              </div>
              <span className="text-xs font-bold text-slate-400">
                {filteredProducts.length} Products Found
              </span>
            </div>

            {filteredProducts.length === 0 ? (
              <div className="text-center py-16 bg-white rounded-3xl border border-dashed border-slate-200 p-4">
                <Package className="w-10 h-10 mx-auto text-slate-300 mb-2" />
                <p className="text-xs font-bold text-slate-400">No products found in this category.</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {filteredProducts.map((p: any) => (
                  <BranchProductCard
                    key={p.id}
                    product={p}
                    primaryColor={primaryColor}
                    onSelectProduct={(prod) => setSelectedProduct(prod)}
                    onBuyNowClick={() => setIsCheckoutViewOpen(true)}
                  />
                ))}
              </div>
            )}
          </section>

          <BranchCartDrawer branchName={branch.name} primaryColor={primaryColor} />

          <BranchAuthModal
            isOpen={isAuthModalOpen}
            onClose={() => setIsAuthModalOpen(false)}
            branch={branch}
            subdomain={subdomain}
            primaryColor={primaryColor}
            onSuccess={(u) => setBranchUser(u)}
          />

          <footer className="border-t border-slate-200 bg-white py-8 px-4 text-center text-xs text-slate-500 space-y-2">
            <p className="font-bold text-slate-700">{branch.name} — Branch Storefront</p>
            <p>
              {branch.address || "Dhaka, Bangladesh"} • {branch.contact_phone}
            </p>
            <p className="text-[10px] text-slate-400">Powered by Xylo SaaS Multi-Store Platform</p>
          </footer>
        </div>
      )}
    </BranchCartProvider>
  );
}